import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Loader,
  Alert,
  Button,
  Avatar,
  Divider,
  TextInput,
  ActionIcon,
  ScrollArea,
  Modal
} from '@mantine/core';
import {
  IconArrowLeft,
  IconSend,
  IconAlertCircle,
  IconUser,
  IconCrown,
  IconRefresh
} from '@tabler/icons-react';
import { api } from '../services/api';
import messagingWebSocket from '../services/messagingWebSocket';

const ConversationPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [canMessage, setCanMessage] = useState(false);
  const [canMessageReason, setCanMessageReason] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const messagesEndRef = useRef(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutRef = useRef({});

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    checkPermissions();
    fetchConversation();
    fetchUserSubscription();
    
    // Connect to WebSocket
    messagingWebSocket.connect();
    
    // Set up WebSocket handlers
    const unsubscribeMessage = messagingWebSocket.onMessage(handleWebSocketMessage);
    const unsubscribeConnection = messagingWebSocket.onConnectionChange(setIsConnected);
    const unsubscribeTyping = messagingWebSocket.onTyping(handleTypingIndicator);
    
    // Cleanup on unmount
    return () => {
      unsubscribeMessage();
      unsubscribeConnection();
      unsubscribeTyping();
    };
  }, [isAuthenticated, navigate, userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWebSocketMessage = (message) => {
    // Only add messages that are part of this conversation
    const messageUserId = message.sender === user.id ? message.recipient : message.sender;
    if (messageUserId === parseInt(userId)) {
      setMessages(prev => {
        // Check if message already exists to avoid duplicates
        const exists = prev.some(m => m.id === message.id);
        if (!exists) {
          // Mark as read if it's from the other user
          if (message.sender !== user.id && !message.is_read) {
            messagingWebSocket.markMessageAsRead(message.id);
          }
          return [...prev, message];
        }
        return prev;
      });
    }
  };
  
  const handleTypingIndicator = ({ senderId, senderUsername, isTyping }) => {
    if (senderId === parseInt(userId)) {
      if (isTyping) {
        setTypingUsers(prev => ({ ...prev, [senderId]: senderUsername }));
        
        // Clear typing indicator after 3 seconds
        if (typingTimeoutRef.current[senderId]) {
          clearTimeout(typingTimeoutRef.current[senderId]);
        }
        typingTimeoutRef.current[senderId] = setTimeout(() => {
          setTypingUsers(prev => {
            const newTyping = { ...prev };
            delete newTyping[senderId];
            return newTyping;
          });
        }, 3000);
      } else {
        setTypingUsers(prev => {
          const newTyping = { ...prev };
          delete newTyping[senderId];
          return newTyping;
        });
      }
    }
  };

  const fetchUserSubscription = async () => {
    try {
      const response = await api.getSubscription();
      if (response.plan) {
        setUserSubscription(response);
      }
    } catch (err) {
      console.log('No subscription found');
    }
  };

  const checkPermissions = async () => {
    try {
      const response = await api.canMessageUser(userId);
      setCanMessage(response.can_message);
      setCanMessageReason(response.reason);
      
      // If user can't message and isn't staff, show upgrade modal
      if (!response.can_message && !user?.is_staff) {
        setShowUpgradeModal(true);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setCanMessage(false);
    }
  };

  const fetchConversation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getConversation(userId);
      setMessages(response.messages);
      setOtherUser(response.other_user);
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError(err.message || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    // Check permissions again before sending
    if (!canMessage && !user?.is_staff) {
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      setSending(true);
      setError(null);
      
      // Try WebSocket first if connected, otherwise use HTTP
      const sentViaWs = isConnected && messagingWebSocket.sendMessage(parseInt(userId), newMessage.trim());
      
      if (!sentViaWs) {
        // Use HTTP API (works even without WebSocket)
        const response = await api.sendMessage(userId, newMessage.trim());
        setMessages(prev => [...prev, response.data]);
      }
      
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message');
      
      // If error is about subscription, show upgrade modal
      if (err.message?.includes('subscription')) {
        setShowUpgradeModal(true);
      }
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Container size="md" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg" style={{ height: 'calc(100vh - 200px)' }}>
        {/* Header */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group justify="space-between">
            <Group gap="sm">
              <ActionIcon 
                onClick={() => navigate('/messages')} 
                variant="light"
                size="lg"
              >
                <IconArrowLeft size="1.2rem" />
              </ActionIcon>
              <Avatar size="md" color="blue">
                <IconUser size="1.5rem" />
              </Avatar>
              <div>
                <Group gap="xs">
                  <Text fw={600}>{otherUser?.username}</Text>
                  {otherUser?.is_seller && (
                    <Badge color="pink" variant="light" size="sm">
                      Princess
                    </Badge>
                  )}
                  {otherUser?.is_staff && (
                    <Badge color="blue" variant="light" size="sm">
                      Staff
                    </Badge>
                  )}
                </Group>
                {otherUser?.business_name && (
                  <Text size="sm" c="dimmed">{otherUser.business_name}</Text>
                )}
                {Object.keys(typingUsers).length > 0 && (
                  <Text size="xs" c="blue" italic>
                    {Object.values(typingUsers)[0]} is typing...
                  </Text>
                )}
              </div>
            </Group>
            <Group gap="xs">
              {isConnected && (
                <Badge color="green" variant="dot" size="sm">
                  Live
                </Badge>
              )}
              <ActionIcon 
                onClick={fetchConversation} 
                variant="light"
                size="lg"
                disabled={loading}
              >
                <IconRefresh size="1.2rem" />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>

        {error && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red">
            {error}
          </Alert>
        )}

        {/* Messages */}
        <Paper shadow="sm" p="md" radius="md" withBorder style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <ScrollArea style={{ flex: 1 }} offsetScrollbars>
            <Stack gap="md" p="sm">
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <Text c="dimmed">No messages yet. Start the conversation!</Text>
                </div>
              ) : (
                messages.map((message) => {
                  const isSent = message.sender === user.id;
                  return (
                    <div
                      key={message.id}
                      style={{
                        display: 'flex',
                        justifyContent: isSent ? 'flex-end' : 'flex-start',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '0.75rem 1rem',
                          borderRadius: '12px',
                          backgroundColor: isSent ? '#228be6' : '#f1f3f5',
                          color: isSent ? 'white' : 'inherit'
                        }}
                      >
                        <Text size="sm" style={{ wordBreak: 'break-word' }}>
                          {message.content}
                        </Text>
                        <Text 
                          size="xs" 
                          c={isSent ? 'rgba(255,255,255,0.7)' : 'dimmed'}
                          mt={4}
                        >
                          {formatMessageTime(message.created_at)}
                          {isSent && message.is_read && ' • Read'}
                        </Text>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Stack>
          </ScrollArea>
        </Paper>

        {/* Message Input */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          {canMessage || user?.is_staff ? (
            <Group gap="sm">
              <TextInput
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.currentTarget.value);
                  // Send typing indicator
                  if (e.currentTarget.value && isConnected) {
                    messagingWebSocket.sendTypingIndicator(parseInt(userId), true);
                  } else if (!e.currentTarget.value && isConnected) {
                    messagingWebSocket.sendTypingIndicator(parseInt(userId), false);
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                style={{ flex: 1 }}
                disabled={sending}
              />
              <ActionIcon 
                onClick={sendMessage} 
                variant="filled"
                size="lg"
                disabled={!newMessage.trim() || sending}
                loading={sending}
              >
                <IconSend size="1.2rem" />
              </ActionIcon>
            </Group>
          ) : (
            <Alert color="yellow" variant="light">
              <Group gap="sm">
                <IconCrown size="1.2rem" />
                <div>
                  <Text fw={500}>Premium Feature</Text>
                  <Text size="sm">{canMessageReason || 'You need a subscription to message this user.'}</Text>
                </div>
              </Group>
              <Button
                mt="sm"
                variant="gradient"
                gradient={{ from: 'blue', to: 'grape' }}
                leftSection={<IconCrown size="1rem" />}
                onClick={() => navigate('/subscription')}
              >
                Upgrade to PayPiggiePro
              </Button>
            </Alert>
          )}
        </Paper>
      </Stack>

      {/* Upgrade Modal */}
      <Modal
        opened={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to PayPiggiePro"
        centered
      >
        <Stack gap="md">
          <Group gap="sm">
            <IconCrown size="2rem" color="var(--mantine-color-yellow-6)" />
            <div>
              <Text fw={500}>Premium Feature</Text>
              <Text size="sm" c="dimmed">
                Messaging princesses is only available for PayPiggiePro subscribers
              </Text>
            </div>
          </Group>
          <Alert color="blue" variant="light">
            <Text size="sm">
              Upgrade to PayPiggiePro to unlock:
            </Text>
            <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              <li>Direct messaging with all princesses</li>
              <li>Priority customer support</li>
              <li>Exclusive deals and discounts</li>
              <li>Early access to new products</li>
            </ul>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowUpgradeModal(false)}
            >
              Maybe Later
            </Button>
            <Button
              variant="gradient"
              gradient={{ from: 'blue', to: 'grape' }}
              leftSection={<IconCrown size="1rem" />}
              onClick={() => navigate('/subscription')}
            >
              Upgrade Now - $25/month
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default ConversationPage;